import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCreateSovChangeRequest } from "@/hooks/useSovChangeRequests";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  sovLine: any;
  projectId: string;
  hasBilling: boolean; // true if bill_lines exist for this sov_line
  hasWorkOrders: boolean; // true if work_order_lines exist for this sov_line
  onComplete: () => void;
}

export const SovEditDialog = ({ open, onOpenChange, sovLine, projectId, hasBilling, hasWorkOrders, onComplete }: Props) => {
  const { user } = useAuth();
  const createRequest = useCreateSovChangeRequest();

  const [description, setDescription] = useState(sovLine?.description ?? "");
  const [quantity, setQuantity] = useState(String(sovLine?.quantity ?? ""));
  const [laborRate, setLaborRate] = useState(String(sovLine?.labor_unit_price ?? "0"));
  const [materialRate, setMaterialRate] = useState(String(sovLine?.material_unit_price ?? "0"));
  const [apLaborRate, setApLaborRate] = useState(String(sovLine?.labor_vendor_rate ?? "0"));
  const [apMaterialRate, setApMaterialRate] = useState(String(sovLine?.material_vendor_rate ?? "0"));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [isPassthrough, setIsPassthrough] = useState(!!sovLine?.is_passthrough);

  useEffect(() => {
    setDescription(sovLine?.description ?? "");
    setQuantity(String(sovLine?.quantity ?? ""));
    setLaborRate(String(sovLine?.labor_unit_price ?? "0"));
    setMaterialRate(String(sovLine?.material_unit_price ?? "0"));
    setApLaborRate(String(sovLine?.labor_vendor_rate ?? "0"));
    setApMaterialRate(String(sovLine?.material_vendor_rate ?? "0"));
    setReason("");
    setIsPassthrough(!!sovLine?.is_passthrough);
  }, [sovLine]);

  const oldQty = Number(sovLine?.quantity ?? 0);
  const oldLabor = Number(sovLine?.labor_unit_price ?? 0);
  const oldMat = Number(sovLine?.material_unit_price ?? 0);
  const oldTotal = oldQty * (oldLabor + oldMat);

  const newQty = Number(quantity) || 0;
  const newLabor = Number(laborRate) || 0;
  const newMat = Number(materialRate) || 0;
  const newTotal = newQty * (newLabor + newMat);
  const impact = newTotal - oldTotal;
  const canDirectFixZeroBudgetLine = useMemo(() => {
    if (hasBilling || hasWorkOrders) return false;
    if (oldTotal > 0) return false;

    const sellRateIncreased = newLabor + newMat > oldLabor + oldMat;
    const quantityChanged = newQty !== oldQty;

    return sellRateIncreased || quantityChanged;
  }, [hasBilling, hasWorkOrders, oldTotal, newLabor, newMat, oldLabor, oldMat, newQty, oldQty]);

  const isDescriptionOnly = useMemo(() => {
    return description !== sovLine?.description &&
      newQty === oldQty &&
      newLabor === oldLabor &&
      newMat === oldMat;
  }, [description, sovLine, newQty, oldQty, newLabor, oldLabor, newMat, oldMat]);

  const isFinancialChange = !isDescriptionOnly && (
    newQty !== oldQty || newLabor !== oldLabor || newMat !== oldMat
  );

  const apLaborChanged = Number(apLaborRate) !== Number(sovLine?.labor_vendor_rate ?? 0);
  const apMaterialChanged = Number(apMaterialRate) !== Number(sovLine?.material_vendor_rate ?? 0);
  const vendorRateChanged = apLaborChanged || apMaterialChanged;
  const passthroughChanged = isPassthrough !== !!sovLine?.is_passthrough;
  const hasAnyChange = description !== sovLine?.description || isFinancialChange || vendorRateChanged || passthroughChanged;

  const fieldChanged = useMemo(() => {
    if (isDescriptionOnly) return "description";
    const qtyChanged = newQty !== oldQty;
    const rateChanged = newLabor !== oldLabor || newMat !== oldMat;
    if (qtyChanged && rateChanged) return "both";
    if (qtyChanged) return "quantity";
    return "unit_price";
  }, [isDescriptionOnly, newQty, oldQty, newLabor, oldLabor, newMat, oldMat]);

  const handleSave = async () => {
    if (!hasAnyChange) return;
    setBusy(true);

    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      // Vendor rates changed? Always auto-save (internal cost, no approval needed)
      if (vendorRateChanged) {
        await supabase
          .from("sov_lines")
          .update({
            labor_vendor_rate: Number(apLaborRate),
            material_vendor_rate: Number(apMaterialRate),
          } as any)
          .eq("id", sovLine.id);
      }

      // Passthrough flag changed? Auto-save (non-financial)
      if (passthroughChanged) {
        await supabase
          .from("sov_lines")
          .update({ is_passthrough: isPassthrough } as any)
          .eq("id", sovLine.id);
      }

      const descChanged = description !== sovLine?.description;
      
      if (!isFinancialChange) {
        // Tier 1: No financial change — auto-save description + sub rate
        if (descChanged) {
          const { error } = await supabase
            .from("sov_lines")
            .update({ description } as any)
            .eq("id", sovLine.id);
          if (error) throw error;

          await supabase.from("ops_audit_log").insert({
            company_id: companyId,
            table_name: "sov_lines",
            record_id: sovLine.id,
            action: "description_correction",
            old_status: sovLine.description,
            new_status: description,
            changed_by: user?.id,
          } as any);
        }

        toast.success(descChanged ? "Description updated" : "Vendor rates updated");
        onOpenChange(false);
        onComplete();
      } else if (canDirectFixZeroBudgetLine) {
        const { error } = await supabase
          .from("sov_lines")
          .update({
            description,
            quantity: newQty,
            labor_unit_price: newLabor,
            material_unit_price: newMat,
            unit_price: newLabor + newMat,
            scheduled_value: newTotal,
          } as any)
          .eq("id", sovLine.id);

        if (error) throw error;

        await supabase.from("ops_audit_log").insert({
          company_id: companyId,
          table_name: "sov_lines",
          record_id: sovLine.id,
          action: "zero_budget_line_fix",
          old_status: JSON.stringify({
            description: sovLine.description,
            quantity: oldQty,
            labor_unit_price: oldLabor,
            material_unit_price: oldMat,
            scheduled_value: oldTotal,
          }),
          new_status: JSON.stringify({
            description,
            quantity: newQty,
            labor_unit_price: newLabor,
            material_unit_price: newMat,
            scheduled_value: newTotal,
          }),
          changed_by: user?.id,
        } as any);

        toast.success("Sell rate updated");
        onOpenChange(false);
        onComplete();
      } else {
        // Tier 2+: Financial change — create approval request
        if (!reason.trim()) {
          toast.error("Reason is required for financial changes");
          setBusy(false);
          return;
        }

        await createRequest.mutateAsync({
          companyId,
          projectId,
          sovLineId: sovLine.id,
          requestedBy: user!.id,
          fieldChanged,
          oldDescription: sovLine.description,
          newDescription: description !== sovLine.description ? description : undefined,
          oldQuantity: oldQty,
          newQuantity: newQty !== oldQty ? newQty : undefined,
          oldLaborUnitPrice: oldLabor,
          newLaborUnitPrice: newLabor !== oldLabor ? newLabor : undefined,
          oldMaterialUnitPrice: oldMat,
          newMaterialUnitPrice: newMat !== oldMat ? newMat : undefined,
          oldTotal,
          newTotal,
          reason: reason.trim(),
        });

        toast.success("SOV change submitted for approval");
        onOpenChange(false);
        onComplete();
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!sovLine) return null;

  // Tier 4: Blocked if billed
  if (hasBilling) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-destructive" /> Line Locked
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">This SOV line cannot be edited — billing has occurred against it.</p>
            <p className="text-sm text-muted-foreground">Use the Change Order process to modify contracted scope after billing.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit SOV Line</DialogTitle>
          <DialogDescription>
            Line #{sovLine.line_number}: {sovLine.description}
            {hasWorkOrders && (
              <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-300">
                <AlertTriangle className="h-3 w-3 mr-1" /> Work orders issued
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Linked Product & Accounts (read-only) */}
          {(sovLine.product_service_id || sovLine.income_account || sovLine.cogs_account) && (
            <div className="flex flex-wrap gap-2 text-xs">
              {sovLine.product_service_id && (
                <Badge variant="secondary" className="text-[10px]">
                  Catalog Item
                </Badge>
              )}
              {sovLine.income_account && (
                <Badge variant="outline" className="text-[10px]">
                  Income: {sovLine.income_account}
                </Badge>
              )}
              {sovLine.cogs_account && (
                <Badge variant="outline" className="text-[10px]">
                  COGS: {sovLine.cogs_account}
                </Badge>
              )}
            </div>
          )}

          {/* Description */}
          <div className="space-y-1">
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          {/* Pass-through toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={isPassthrough}
              onCheckedChange={(checked) => setIsPassthrough(!!checked)}
            />
            <span className="text-sm">Pass-through (no margin required)</span>
            <span className="text-xs text-muted-foreground">— e.g. fuel, mobilization</span>
          </label>

          {/* Financial fields */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Quantity</Label>
              <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>AP Labor Rate <span className="text-xs text-muted-foreground">(vendor)</span></Label>
              <Input type="number" step="0.01" value={apLaborRate} onChange={(e) => setApLaborRate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>AP Material Rate <span className="text-xs text-muted-foreground">(vendor)</span></Label>
              <Input type="number" step="0.01" value={apMaterialRate} onChange={(e) => setApMaterialRate(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Labor Rate <span className="text-xs text-muted-foreground">(billed)</span></Label>
              <Input type="number" step="0.01" value={laborRate} onChange={(e) => setLaborRate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Material Rate <span className="text-xs text-muted-foreground">(billed)</span></Label>
              <Input type="number" step="0.01" value={materialRate} onChange={(e) => setMaterialRate(e.target.value)} />
            </div>
          </div>

          {/* Change summary */}
          {isFinancialChange && (
            <div className={cn(
              "rounded-md border p-3 text-sm space-y-1",
              impact < 0 ? "border-destructive/30 bg-destructive/5" : "border-primary/30 bg-primary/5"
            )}>
              <p className="font-medium flex items-center gap-1">
                <ShieldCheck className="h-4 w-4" /> {canDirectFixZeroBudgetLine ? "Zero-budget correction — will save immediately" : "Financial Change — Requires Approval"}
              </p>
              {newQty !== oldQty && (
                <p>Qty: <span className="line-through text-muted-foreground">{oldQty.toLocaleString()}</span> → <span className="font-semibold">{newQty.toLocaleString()}</span></p>
              )}
              {newLabor !== oldLabor && (
                <p>Labor: <span className="line-through text-muted-foreground">{fmt(oldLabor)}</span> → <span className="font-semibold">{fmt(newLabor)}</span></p>
              )}
              {newMat !== oldMat && (
                <p>Material: <span className="line-through text-muted-foreground">{fmt(oldMat)}</span> → <span className="font-semibold">{fmt(newMat)}</span></p>
              )}
              <p className={cn("font-semibold", impact < 0 ? "text-destructive" : "text-primary")}>
                Impact: {impact >= 0 ? "+" : ""}{fmt(impact)} to contract value
              </p>
              {hasWorkOrders && !canDirectFixZeroBudgetLine && (
                <p className="text-yellow-600 text-xs">⚠ Work orders are issued against this line. Controller + PM will be notified.</p>
              )}
              {canDirectFixZeroBudgetLine && (
                <p className="text-muted-foreground text-xs">This line has no billing or work orders yet, so the initial sell rate can be corrected directly.</p>
              )}
            </div>
          )}

          {!isFinancialChange && hasAnyChange && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
              <p className="font-medium text-primary">
                {isDescriptionOnly ? "Description correction" : vendorRateChanged ? "Vendor rate update" : "Non-financial update"} — auto-approved
              </p>
              <p className="text-muted-foreground text-xs">No financial impact to contract. Will be logged to audit trail.</p>
            </div>
          )}

          {/* Reason (required for financial changes) */}
          {isFinancialChange && !canDirectFixZeroBudgetLine && (
            <div className="space-y-1">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why this change is needed..."
                className="min-h-[80px]"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
              disabled={busy || !hasAnyChange || (isFinancialChange && !canDirectFixZeroBudgetLine && !reason.trim())}
          >
              {busy ? "Saving…" : isFinancialChange ? (canDirectFixZeroBudgetLine ? "Save" : "Submit for Approval →") : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
